import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MonitorPlay, Plus, Search } from 'lucide-react';
import { useAllInstances, useActiveInstances } from '../hooks/useInstances';
import type { Instance } from '../types/instance';
import InstanceLaunchModal from '../components/InstanceLaunchModal';

const statusColor: Record<string, string> = {
  running: 'bg-green-500',
  starting: 'bg-blue-500',
  paused: 'bg-yellow-500',
  completed: 'bg-slate-400',
  failed: 'bg-red-500',
  stopped: 'bg-slate-400',
  pending: 'bg-slate-300',
};

const tabs = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'failed', label: 'Failed' },
] as const;

type TabKey = (typeof tabs)[number]['key'];

function formatCost(n: number | string): string {
  const v = Number(n);
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function InstanceCard({ instance, onClick }: { instance: Instance; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-xl border border-slate-200 bg-white p-4 text-left transition-colors hover:border-slate-300"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full ${statusColor[instance.status] ?? 'bg-slate-300'}`} />
          <span className="text-sm font-medium text-slate-900">
            {instance.agent?.name ?? 'Unknown agent'}
          </span>
        </div>
        <span className="text-xs capitalize text-slate-400">{instance.status}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
          {instance.project?.name ?? 'Unknown project'}
        </span>
      </div>
      <p className="mt-1.5 truncate text-xs text-slate-500">
        {instance.task?.title ?? instance.prompt?.slice(0, 80) ?? 'No prompt'}
      </p>
      <div className="mt-2 flex gap-3 text-xs text-slate-400">
        <span>{instance.model}</span>
        <span>Turn {instance.currentTurn}/{instance.maxTurns}</span>
        <span>{formatCost(instance.totalCostUsd)}</span>
      </div>
      <div className="mt-1 text-xs text-slate-300">
        {relativeTime(instance.updatedAt)}
      </div>
    </button>
  );
}

export default function GlobalInstances() {
  const [tab, setTab] = useState<TabKey>('all');
  const [search, setSearch] = useState('');
  const [showLaunch, setShowLaunch] = useState(false);
  const navigate = useNavigate();

  const statusFilter = tab === 'all' ? undefined : tab === 'active' ? undefined : tab;
  const { data: allInstances, isLoading: allLoading } = useAllInstances(statusFilter);
  const { data: activeInstances, isLoading: activeLoading } = useActiveInstances();

  const instances = tab === 'active' ? activeInstances : allInstances;
  const isLoading = tab === 'active' ? activeLoading : allLoading;

  const filtered = (instances ?? []).filter((inst) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      inst.agent?.name?.toLowerCase().includes(q) ||
      inst.project?.name?.toLowerCase().includes(q) ||
      inst.task?.title?.toLowerCase().includes(q) ||
      inst.model?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Instances</h1>
          <p className="mt-1 text-sm text-slate-500">All instances across your projects</p>
        </div>
        <button
          onClick={() => setShowLaunch(true)}
          className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" />
          Launch Instance
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex items-center gap-4 border-b border-slate-200">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by agent, project, task, or model..."
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-400 focus:outline-none"
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading instances...</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <MonitorPlay className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="text-sm text-slate-500">
            {search ? 'No instances match your search.' : 'No instances found.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((inst) => (
            <InstanceCard
              key={inst.id}
              instance={inst}
              onClick={() => navigate(`/instances/${inst.id}`)}
            />
          ))}
        </div>
      )}

      <InstanceLaunchModal open={showLaunch} onClose={() => setShowLaunch(false)} />
    </div>
  );
}
